import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MobileStaffLayout from "../../components/mobile-staff/MobileStaffLayout";
import api from "../../api/client";
import { fmtXOF, UNITE_STATUTS } from "../../lib/format";
import { Building2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUT_COLORS = {
  disponible: "bg-[#E5F0E8] text-[#2D5A3C]",
  reserve: "bg-[#FFF4E5] text-[#7A5A1A]",
  vendu: "bg-[#FBEAEA] text-[#7A2D2D]",
  loue: "bg-[#E8EBFA] text-[#1E3A8A]",
  travaux: "bg-[#F1F3FB] text-[#4B5775]",
};

export default function StaffBiens() {
  const [unites, setUnites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statut, setStatut] = useState("disponible");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/unites", { params: statut === "all" ? {} : { statut } });
        setUnites(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [statut]);

  const changeStatut = async (uniteId, newStatut, uniteName) => {
    try {
      await api.patch(`/unites/${uniteId}`, { statut: newStatut });
      const label = UNITE_STATUTS[newStatut]?.label || newStatut;
      toast.success(`"${uniteName}" → ${label}`);
      setUnites((prev) => prev.map((u) => (u.id === uniteId ? { ...u, statut: newStatut } : u)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filtered = unites.filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (u.nom || "").toLowerCase().includes(q) || (u.immeuble?.nom || "").toLowerCase().includes(q);
  });

  const TABS = [
    { v: "disponible", l: "Disponible" },
    { v: "reserve", l: "Réservé" },
    { v: "vendu", l: "Vendu" },
    { v: "loue", l: "Loué" },
    { v: "all", l: "Tous" },
  ];

  return (
    <MobileStaffLayout title="Biens">
      <div className="px-5 pt-5 pb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#E91E63] font-bold mb-1">// CATALOGUE</div>
        <h2 className="font-display text-2xl font-extrabold text-[#0F1A3D] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          Biens immobiliers
        </h2>
        <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
          {TABS.map((t) => (
            <button
              key={t.v}
              onClick={() => setStatut(t.v)}
              className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border-2 transition ${statut === t.v ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[#E2E5F1] bg-white text-[#4B5775]"}`}
              data-testid={`ms-biens-tab-${t.v}`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher un bien…" className="w-full h-10 pl-9 pr-3 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]" data-testid="ms-biens-search" />
        </div>
      </div>

      <div className="px-5 space-y-2">
        {loading ? (
          <div className="text-center py-8 text-[#4B5775]"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E2E5F1] rounded-xl p-8 text-center">
            <Building2 className="w-8 h-8 text-[#1E3A8A] mx-auto opacity-50 mb-2" />
            <div className="text-sm font-bold text-[#0F1A3D]">Aucun bien dans cette catégorie</div>
          </div>
        ) : (
          filtered.map((u) => (
            <div
              key={u.id}
              className="bg-white border border-[#E2E5F1] rounded-xl p-3 flex items-center gap-3"
              data-testid={`ms-bien-${u.id}`}
            >
              <Link to={`/immeubles/${u.immeuble_id}`} className="flex-1 min-w-0 flex items-center gap-3 active:opacity-70">
                <div className="w-11 h-11 rounded-md bg-[#F1F3FB] text-[#1E3A8A] flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[#0F1A3D] truncate">{u.nom}</div>
                  <div className="text-[11px] text-[#4B5775] truncate">{u.immeuble?.nom || u.type_unite} · {u.surface_m2 ? u.surface_m2 + " m²" : "—"}</div>
                  <div className="text-[11px] font-bold text-[#0F1A3D] font-mono mt-0.5">{u.prix_vente ? fmtXOF(u.prix_vente) : (u.prix_location ? fmtXOF(u.prix_location) + "/mois" : "—")}</div>
                </div>
              </Link>
              <select
                value={u.statut}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  const v = e.target.value;
                  if (v !== u.statut && window.confirm(`Changer "${u.nom}" en ${(UNITE_STATUTS[v]?.label || v).toUpperCase()} ?`)) {
                    changeStatut(u.id, v, u.nom);
                  }
                }}
                className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 h-7 rounded border-0 cursor-pointer ${STATUT_COLORS[u.statut] || "bg-[#F1F3FB] text-[#4B5775]"}`}
                data-testid={`ms-bien-statut-${u.id}`}
                title="Changer le statut"
              >
                {Object.entries(UNITE_STATUTS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
          ))
        )}
      </div>
    </MobileStaffLayout>
  );
}
